import { Card, CardContent } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import AccountPermissionGroup from "@saleor/components/AccountPermissionGroup";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import { ControlledCheckbox } from "@saleor/components/ControlledCheckbox";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { StaffErrorFragment } from "@saleor/fragments/types/StaffErrorFragment";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { commonMessages } from "@saleor/intl";
import { SearchBrands_search_edges_node } from "@saleor/searches/types/SearchBrands";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { SearchPermissionGroups_search_edges_node } from "@saleor/searches/types/SearchPermissionGroups";
import { SearchTenantsByBrandId_search_edges_node } from "@saleor/searches/types/SearchTenantsByBrandId";
import { StaffListUrlQueryParams } from "@saleor/staff/urls";
import { FetchMoreProps, SearchPageProps } from "@saleor/types";
import { CountryCode } from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getAccountErrorMessage from "@saleor/utils/errors/staff";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import { useIntl } from "react-intl";

import { getBrandChoices, getMallChoices, getTenantChoices } from "../../utils";
import StaffAssignmentDropdowns from "../StaffAssignmentDropdowns";
import { PromoCreatorTypeEnum } from "../StaffCreateProperties/constants";
import StaffCreateProperties from "../StaffCreateProperties/StaffCreateProperties";
import StaffMallList from "../StaffMallList/StaffMallList";
import {
  hasNoError,
  useAccountValidation
} from "./../StaffDetailsPage/Validations";

const useStyles = makeStyles(
  theme => ({
    prop: {
      marginBottom: theme.spacing(2)
    },
    propGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(1),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(4),
      gridTemplateColumns: "120px 1fr"
    }
  }),
  { name: "StaffCreateProperties" }
);

export interface StaffDetailsFormData {
  mallId: string;
  brandId: string;
  tenantId: string;
  email: string;
  firstName: string;
  isActive: boolean;
  lastName: string;
  contactNo: string;
  password: string;
  permissionGroup: string;
  companyName: string;
  malls: string[];
  streetBrgy: string;
  provinceCode: string;
  provinceName: string;
  city: string;
  country: CountryCode;
  mapPlaceId: string;
  postalCode: string;
  promoCreator: string;
}

export interface StaffDetailsPageProps extends SearchPageProps {
  availablePermissionGroups: SearchPermissionGroups_search_edges_node[];
  showBrands: boolean;
  showMalls: boolean;
  showTenants: boolean;
  disabled: boolean;
  fetchMorePermissionGroups: FetchMoreProps;
  saveButtonBarState: ConfirmButtonTransitionState;
  errors: StaffErrorFragment[];
  mallChoices: SearchMalls_search_edges_node[];
  brandChoices: SearchBrands_search_edges_node[];
  tenantChoices: SearchTenantsByBrandId_search_edges_node[];
  fetchMalls: (query: string) => void;
  fetchBrands: (query: string) => void;
  fetchTenants: (query: string) => void;
  onBack: () => void;
  handleUserTypeChange: (selectedValue) => void;
  onCouponMallAssign?: () => void;
  onSubmit: (data: StaffDetailsFormData) => SubmitPromise;
  fetchTenantsByBrandId: (id) => void;
  fetchMoreMalls?: FetchMoreProps;
  fetchMoreBrands?: FetchMoreProps;
  fetchMoreTenants?: FetchMoreProps;
  isRunnerRider: boolean;
  hasManageCouponsPermission: boolean;
  params: StaffListUrlQueryParams;
  selectedPermissionGroup: string;
}

const StaffCreatePage: React.FC<StaffDetailsPageProps> = ({
  mallChoices: mallList,
  brandChoices: brandList,
  tenantChoices: tenantList,
  fetchMalls,
  fetchBrands,
  fetchTenants,
  fetchMoreMalls,
  fetchMoreBrands,
  fetchMoreTenants,
  handleUserTypeChange,
  fetchTenantsByBrandId,
  showBrands,
  showMalls,
  showTenants,
  availablePermissionGroups,
  disabled,
  errors,
  fetchMorePermissionGroups,
  initialSearch,
  onBack,
  onSearchChange,
  onSubmit,
  saveButtonBarState,
  isRunnerRider,
  hasManageCouponsPermission,
  selectedPermissionGroup
}: StaffDetailsPageProps) => {
  const intl = useIntl();
  const classes = useStyles({});

  const formFields = ["password", "email"];
  const formErrors = getFormErrors(formFields, errors || []);
  const [selectedMalls, setSelectedMalls] = React.useState([]);
  const initialForm: StaffDetailsFormData = {
    brandId: "",
    city: "",
    companyName: "",
    contactNo: "639",
    country: CountryCode.PH,
    email: "",
    firstName: "",
    isActive: false,
    lastName: "",
    mallId: "",
    malls: selectedMalls,
    mapPlaceId: "home",
    password: "",
    permissionGroup: "",
    postalCode: "",
    promoCreator: PromoCreatorTypeEnum.SMO_MARKETING,
    provinceCode: "",
    provinceName: "",
    streetBrgy: "",
    tenantId: ""
  };

  const [mallDisplayValue, setMallDisplayValue] = React.useState("");
  const [brandDisplayValue, setBrandDisplayValue] = React.useState("");
  const [tenantDisplayValue, setTenantDisplayValue] = React.useState("");
  const mallChoices = getMallChoices(mallList);
  const brandChoices = getBrandChoices(brandList);
  const tenantChoices = getTenantChoices(tenantList);

  const [validationError, setValidationError] = React.useState({
    city: null,
    companyName: null,
    contactNo: null,
    email: null,
    firstName: null,
    lastName: null,
    postalCode: null,
    provinceCode: null,
    streetBrgy: null
  });

  const handleSubmit = event => {
    setValidationError({
      city: null,
      companyName: null,
      contactNo: null,
      email: null,
      firstName: null,
      lastName: null,
      postalCode: null,
      provinceCode: null,
      streetBrgy: null
    });
    const validate = useAccountValidation(event, isRunnerRider);
    if (hasNoError(validate)) {
      onSubmit(event);
    } else {
      setValidationError(validate);
    }
  };

  const handleSelectedMallsChange = newSelectedMalls => {
    setSelectedMalls(newSelectedMalls);
  };

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit, hasChanged }) => {
        const availablePermissionGroupsChoices =
          availablePermissionGroups?.map(group => ({
            label: group.name,
            value: group.id
          })) || [];

        const permissionGroupChange = createSingleAutocompleteSelectHandler(
          change,
          selectedValue => {
            handleUserTypeChange(selectedValue);
            // We always clear assignment fields after changing User Type dropdown selection
            setMallDisplayValue("");
            setBrandDisplayValue("");
            setTenantDisplayValue("");

            change({ target: { name: "brandId", value: "" } });
            change({ target: { name: "mallId", value: "" } });
            change({ target: { name: "tenantId", value: "" } });
          },
          availablePermissionGroupsChoices
        );

        const [selectedMalls, setSelectedMalls] = React.useState([]);

        const mallChange = createSingleAutocompleteSelectHandler(
          change,
          setMallDisplayValue,
          mallChoices
        );
        const brandChange = createSingleAutocompleteSelectHandler(
          change,
          setBrandDisplayValue,
          brandChoices
        );
        const tenantChange = createSingleAutocompleteSelectHandler(
          change,
          setTenantDisplayValue,
          tenantChoices
        );

        const handleChange = event => {
          const { name, value } = event.target;

          if (name === "mallId") {
            setMallDisplayValue(value);
          } else if (name === "brandId") {
            setBrandDisplayValue(value);
            fetchTenantsByBrandId(value);
          } else if (name === "tenantId") {
            setTenantDisplayValue(value);
          }
        };

        const breadcrumbDirectory = [
          {
            name: "Users",
            url: "/staff"
          },
          {
            name: "Create User"
          }
        ];

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader
                title={intl.formatMessage({
                  defaultMessage: "Create User",
                  description: "page header"
                })}
              />
            </div>
            <Grid>
              <div>
                <StaffCreateProperties
                  localError={validationError}
                  errors={errors}
                  data={formData}
                  disabled={disabled}
                  onChange={change}
                  selectedPermissionGroup={selectedPermissionGroup}
                  isRunnerRider={isRunnerRider}
                  hasManageCouponsPermission={hasManageCouponsPermission}
                />
                <CardSpacer />
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Login Credentials",
                      description: "dialog header"
                    })}
                  />
                  <CardContent>
                    <div>
                      <div className={classes.propGrid}>
                        <div className={classes.prop}>
                          <TextField
                            id="emailTxt"
                            error={
                              !!formErrors.email || !!validationError.email
                            }
                            label={intl.formatMessage(commonMessages.email)}
                            value={formData.email}
                            name="email"
                            onChange={change}
                            helperText={
                              (!!validationError.email &&
                                validationError.email) ||
                              getAccountErrorMessage(formErrors.email, intl)
                            }
                            fullWidth
                          />
                        </div>
                        <div className={classes.prop}>
                          <TextField
                            id="passwordTxt"
                            error={!!formErrors.password}
                            helperText={getAccountErrorMessage(
                              formErrors.password,
                              intl
                            )}
                            type="password"
                            label={intl.formatMessage({
                              defaultMessage: "Password",
                              description: "input label"
                            })}
                            value={formData.password}
                            name="password"
                            onChange={change}
                            fullWidth
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <CardSpacer />
                {hasManageCouponsPermission && (
                  <StaffMallList
                    disabled={false}
                    title="Malls"
                    onMallAssign={undefined}
                    onMallUnassign={id => {
                      const newMalls = selectedMalls.filter(
                        mall => mall.node.id !== id
                      );
                      setSelectedMalls(newMalls);
                    }}
                    emptyText="Assigned to all malls"
                    onUpdateSelectedMalls={handleSelectedMallsChange}
                    fetchMoreMalls={fetchMoreMalls}
                    formData={formData}
                  />
                )}
              </div>
              <div>
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Permissions",
                      description: "dialog header"
                    })}
                  />
                  <CardContent>
                    <AccountPermissionGroup
                      formData={formData}
                      disabled={disabled}
                      errors={errors}
                      initialSearch={initialSearch}
                      availablePermissionGroups={availablePermissionGroups}
                      onChange={permissionGroupChange}
                      onSearchChange={onSearchChange}
                      displayValue={selectedPermissionGroup}
                      {...fetchMorePermissionGroups}
                    />
                    <StaffAssignmentDropdowns
                      errors={errors}
                      mallChoices={mallChoices}
                      disabled={disabled}
                      brandChoices={brandChoices}
                      tenantChoices={tenantChoices}
                      mallDisplayValue={mallDisplayValue}
                      brandDisplayValue={brandDisplayValue}
                      tenantDisplayValue={tenantDisplayValue}
                      formData={formData}
                      showBrands={showBrands}
                      showMalls={showMalls}
                      showTenants={showTenants}
                      handleChange={handleChange}
                      mallChange={mallChange}
                      brandChange={brandChange}
                      tenantChange={tenantChange}
                      fetchMalls={query => fetchMalls(query)}
                      fetchBrands={query => fetchBrands(query)}
                      fetchTenants={query => fetchTenants(query)}
                      fetchMoreMalls={fetchMoreMalls}
                      fetchMoreBrands={fetchMoreBrands}
                      fetchMoreTenants={fetchMoreTenants}
                    />
                  </CardContent>
                </Card>
                <CardSpacer />
                <Card>
                  <CardTitle
                    title={intl.formatMessage({
                      defaultMessage: "Status",
                      description: "section header"
                    })}
                  />
                  <CardContent>
                    <ControlledCheckbox
                      id="activeCheckbox"
                      checked={formData.isActive}
                      disabled={disabled}
                      label={intl.formatMessage({
                        defaultMessage: "Active",
                        description: "status label"
                      })}
                      name="isActive"
                      onChange={change}
                    />
                  </CardContent>
                </Card>
              </div>
            </Grid>
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              state={saveButtonBarState}
              onCancel={onBack}
              onSave={submit}
            />
          </Container>
        );
      }}
    </Form>
  );
};
StaffCreatePage.displayName = "StaffCreatePage";
export default StaffCreatePage;
